import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Note, Space } from '../models/space.model';
import type { RoiFields } from '../models/space.model';
import { GlobalSettingsService } from './global-settings.service';
import { RoiCalculatorService } from './roi-calculator.service';
import { SpaceConfigOptionsService } from './space-config-options.service';

const STORAGE_KEY = 'amplify_spaces';

const EMPTY_SPACE: Space = {
  id: '',
  name: '',
  country: '',
  languageCode: 'en',
  currencyCode: 'EUR',
  notes: [],
  relevantIndustries: [],
  disciplines: [],
  relevantMaterials: [],
  selectedSolutions: [],
  selectedEditions: [],
  dateCreated: new Date(),
  ownerName: 'Current User',
  status: 'Active',
  teamSize: 0,
  averageHourlyRate: 0,
  projectsPerYear: 0,
  averageLicenceCost: 0,
  maturityLevel: 20,
  productivityGainPercent: 0,
  reworkReductionPercent: 0,
  coordinationSavingsPercent: 0,
  bookmarkedProductDemos: [],
  bookmarkedWorkflowDemos: [],
  bookmarkedUseCases: []
};

@Injectable({
  providedIn: 'root'
})
export class SpacesService {
  private readonly globalSettingsService = inject(GlobalSettingsService);
  private readonly roiCalculatorService = inject(RoiCalculatorService);
  private readonly spaceConfigOptions = inject(SpaceConfigOptionsService);

  private readonly spaces: Space[] = [];
  private readonly spacesSubject = new BehaviorSubject<Space[]>([]);
  private readonly activeSpaceSubject: BehaviorSubject<Space>;
  private readonly selectedSolutionNamesSubject = new BehaviorSubject<string[]>([]);
  private readonly selectedEditionIdsSubject = new BehaviorSubject<string[]>([]);

  readonly spaces$ = this.spacesSubject.asObservable();
  readonly activeSpace$: Observable<Space>;
  readonly selectedSolutionNames$ = this.selectedSolutionNamesSubject.asObservable();

  constructor() {
    this.activeSpaceSubject = new BehaviorSubject<Space>(this.createEmptySpace());
    this.activeSpace$ = this.activeSpaceSubject.asObservable();
    this.loadFromStorage();
  }

  private createEmptySpace(): Space {
    const settings = this.globalSettingsService.get();
    const countryName = this.spaceConfigOptions.countries.find(c => c.code === settings.countryCode)?.name ?? '';
    const maturityLevel = 20;
    const distribution = this.roiCalculatorService.getWorkflowDistribution(maturityLevel);
    const defaults = this.roiCalculatorService.getImprovementPotential(distribution);
    return {
      ...structuredClone(EMPTY_SPACE),
      id: crypto.randomUUID(),
      country: countryName,
      languageCode: settings.languageCode,
      currencyCode: settings.currencyCode,
      productivityGainPercent: defaults.modelingImprovement,
      reworkReductionPercent: defaults.reworkImprovement,
      coordinationSavingsPercent: defaults.coordinationImprovement
    };
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Space[];
        // Restore Date objects and migrate missing ids
        parsed.forEach(space => {
          if (!space.id) {
            space.id = crypto.randomUUID();
          }
          space.dateCreated = new Date(space.dateCreated);
          // Restore note dates
          if (space.notes) {
            space.notes.forEach(note => {
              note.createdAt = new Date(note.createdAt);
            });
          } else {
            space.notes = [];
          }
          // Migrate: compute percent defaults for old spaces that lack them
          if (space.productivityGainPercent == null || space.reworkReductionPercent == null || space.coordinationSavingsPercent == null) {
            const dist = this.roiCalculatorService.getWorkflowDistribution(space.maturityLevel ?? 20);
            const defs = this.roiCalculatorService.getImprovementPotential(dist);
            space.productivityGainPercent = space.productivityGainPercent ?? defs.modelingImprovement;
            space.reworkReductionPercent = space.reworkReductionPercent ?? defs.reworkImprovement;
            space.coordinationSavingsPercent = space.coordinationSavingsPercent ?? defs.coordinationImprovement;
          }
          // Migrate: bookmark lists for old spaces
          space.bookmarkedProductDemos = space.bookmarkedProductDemos ?? [];
          space.bookmarkedWorkflowDemos = space.bookmarkedWorkflowDemos ?? [];
          space.bookmarkedUseCases = space.bookmarkedUseCases ?? [];
        });
        this.spaces.push(...parsed);
        this.spacesSubject.next([...this.spaces]);
      }
    } catch (e) {
      console.error('Failed to load spaces from localStorage:', e);
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.spaces));
    } catch (e) {
      console.error('Failed to save spaces to localStorage:', e);
    }
  }

  getAll(): Space[] {
    return [...this.spaces];
  }

  getAll$(): Observable<Space[]> {
    return this.spaces$;
  }

  /** Look up by id (primary key). */
  get(id: string): Space | undefined {
    return this.spaces.find(space => space.id === id);
  }

  /** Look up by display name (used only for duplicate-name checks). */
  getByName(name: string): Space | undefined {
    return this.spaces.find(space => space.name === name);
  }

  add(space: Space): boolean {
    if (!space.id) {
      space.id = crypto.randomUUID();
    }
    if (this.existsById(space.id)) {
      return false;
    }
    this.spaces.push(space);
    this.spacesSubject.next([...this.spaces]);
    this.saveToStorage();
    return true;
  }

  remove(id: string): boolean {
    const index = this.spaces.findIndex(space => space.id === id);
    if (index !== -1) {
      this.spaces.splice(index, 1);
      this.spacesSubject.next([...this.spaces]);
      this.saveToStorage();
      return true;
    }
    return false;
  }

  update(id: string, updates: Partial<Space>): boolean {
    const space = this.get(id);
    if (space) {
      Object.assign(space, updates);
      this.spacesSubject.next([...this.spaces]);
      this.saveToStorage();
      return true;
    }
    return false;
  }

  /** Check existence by display name (for duplicate-name validation). */
  exists(name: string): boolean {
    return this.spaces.some(space => space.name === name);
  }

  existsById(id: string): boolean {
    return this.spaces.some(space => space.id === id);
  }

  addNote(spaceId: string, note: Note): boolean {
    const space = this.get(spaceId);
    if (space) {
      space.notes.push(note);
      this.spacesSubject.next([...this.spaces]);
      this.saveToStorage();
      this.activeSpaceSubject.next({ ...space });
      return true;
    }
    return false;
  }

  updateNote(spaceId: string, index: number, text: string): boolean {
    const space = this.get(spaceId);
    if (space && index >= 0 && index < space.notes.length) {
      space.notes[index] = { ...space.notes[index], text };
      this.spacesSubject.next([...this.spaces]);
      this.saveToStorage();
      this.activeSpaceSubject.next({ ...space });
      return true;
    }
    return false;
  }

  deleteNote(spaceId: string, index: number): boolean {
    const space = this.get(spaceId);
    if (space && index >= 0 && index < space.notes.length) {
      space.notes.splice(index, 1);
      this.spacesSubject.next([...this.spaces]);
      this.saveToStorage();
      this.activeSpaceSubject.next({ ...space });
      return true;
    }
    return false;
  }

  // --- Active space ---

  setActiveSpace(id: string): boolean {
    const space = this.get(id);
    if (space) {
      this.activeSpaceSubject.next({ ...space });
      this.selectedSolutionNamesSubject.next(space.selectedSolutions ?? []);
      this.selectedEditionIdsSubject.next(space.selectedEditions ?? []);
      return true;
    }
    return false;
  }

  getActiveSpace(): Space {
    return {
      ...this.activeSpaceSubject.value,
      selectedSolutions: this.selectedSolutionNamesSubject.value,
      selectedEditions: this.selectedEditionIdsSubject.value
    };
  }

  clearActiveSpace(): void {
    this.activeSpaceSubject.next(this.createEmptySpace());
    this.selectedSolutionNamesSubject.next([]);
    this.selectedEditionIdsSubject.next([]);
  }

  updateActiveSpaceIndustries(industries: string[]): void {
    this.activeSpaceSubject.next({ ...this.activeSpaceSubject.value, relevantIndustries: industries });
    const active = this.activeSpaceSubject.value;
    if (active.id) {
      this.update(active.id, { relevantIndustries: industries });
    }
  }

  updateActiveSpaceDisciplines(disciplines: string[]): void {
    this.activeSpaceSubject.next({ ...this.activeSpaceSubject.value, disciplines });
    const active = this.activeSpaceSubject.value;
    if (active.id) {
      this.update(active.id, { disciplines });
    }
  }

  updateActiveSpaceMaterials(materials: string[]): void {
    this.activeSpaceSubject.next({ ...this.activeSpaceSubject.value, relevantMaterials: materials });
    const active = this.activeSpaceSubject.value;
    if (active.id) {
      this.update(active.id, { relevantMaterials: materials });
    }
  }

  updateActiveSpaceCountry(country: string): void {
    this.activeSpaceSubject.next({ ...this.activeSpaceSubject.value, country });
    const active = this.activeSpaceSubject.value;
    if (active.id) {
      this.update(active.id, { country });
    }
  }

  updateActiveSpaceLocaleSettings(settings: { country?: string; languageCode?: string; currencyCode?: string }): void {
    const current = this.activeSpaceSubject.value;
    this.activeSpaceSubject.next({
      ...current,
      ...(settings.country && { country: settings.country }),
      ...(settings.languageCode && { languageCode: settings.languageCode }),
      ...(settings.currencyCode && { currencyCode: settings.currencyCode })
    });
    if (current.id) {
      this.update(current.id, {
        ...(settings.country && { country: settings.country }),
        ...(settings.languageCode && { languageCode: settings.languageCode }),
        ...(settings.currencyCode && { currencyCode: settings.currencyCode })
      });
    }
  }

  updateActiveSpaceRoiField<K extends keyof RoiFields>(field: K, value: RoiFields[K]): void {
    this.activeSpaceSubject.next({ ...this.activeSpaceSubject.value, [field]: value });
  }

  updateActiveSpaceEditions(editionIds: string[]): void {
    this.selectedEditionIdsSubject.next(editionIds);
    const active = this.activeSpaceSubject.value;
    if (active.id) {
      this.update(active.id, { selectedEditions: editionIds });
    }
  }

  updateSelectedSolutions(solutionNames: string[]): void {
    this.selectedSolutionNamesSubject.next(solutionNames);
    const active = this.activeSpaceSubject.value;
    if (active.id) {
      this.update(active.id, { selectedSolutions: solutionNames });
    }
  }

  getSelectedSolutions(): string[] {
    return this.selectedSolutionNamesSubject.value;
  }

  toggleBookmark(category: 'bookmarkedProductDemos' | 'bookmarkedWorkflowDemos' | 'bookmarkedUseCases', id: number): void {
    const active = this.activeSpaceSubject.value;
    const current = (active[category] as Array<string | number>).slice();
    const idx = current.indexOf(id);
    if (idx === -1) {
      current.push(id);
    } else {
      current.splice(idx, 1);
    }
    const updated = { ...active, [category]: current };
    this.activeSpaceSubject.next(updated);
    if (active.id) {
      this.update(active.id, { [category]: current });
    }
  }

  getSelectedEditionIds(): string[] {
    return this.selectedEditionIdsSubject.value;
  }
}
